"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OurCoreServices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const offerings = [{
    number: "01",
    title: "Precision Pruning",
    subtitle: "Structural Integrity // Health",
    description: "Surgical removal of dead or diseased limbs to enhance the structural architecture of your canopy. We utilize technical climbing methods to ensure zero impact on your landscape.",
    inclusions: ["ISA Certified Arborist oversight", "Crown thinning & weight reduction", "Full debris removal & recycling"],
    linkTo: "/OurExpertisePage"
  }, {
    number: "02",
    title: "Technical Removals",
    subtitle: "High-Risk // Confined Space",
    description: "Specialized extraction of hazardous trees in tight residential quarters. Our team employs advanced rigging systems to dismantle massive specimens with millimeter precision.",
    inclusions: ["Complex rigging & crane support", "Underground utility protection", "Stump grinding to 12\" depth"],
    linkTo: "/ResidentialTreeCarePage"
  }, {
    number: "03",
    title: "Plant Health Care",
    subtitle: "Diagnostic // Soil Nutrition",
    description: "Scientific management of the DFW soil profile. We treat Oak Wilt, Emerald Ash Borer, and nutrient deficiencies using deep-root injections and organic soil amendments.",
    inclusions: ["Soil composition analysis", "Pest & disease suppression", "Seasonal growth monitoring"],
    linkTo: "/HoaTreeCarePage"
  }];
  return <section data-section-id="3850" id="our-core-services" className="bg-background text-foreground py-24 md:py-40 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-secondary mb-6 md:mb-8">
            01 // OUR CORE SERVICES
          </p>
          <h2 className="font-default font-extrabold text-foreground leading-[0.95] tracking-tighter" style={{
          fontSize: "clamp(2.5rem, 8vw, 5.5rem)"
        }}>
            Architects of the
            <br />
            <span className="text-accent">DFW Canopy.</span>
          </h2>
        </div>

        <div className="border-t border-foreground/10">
          {offerings.map((offering, i) => {
          const {
            ref,
            isVisible
          } = useRevealOnScroll<HTMLDivElement>();
          return <div key={offering.number} data-index={i} ref={ref} className={`group relative border-b border-foreground/10 transition-all duration-700 ease-out hover:bg-accent/[0.03] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: isVisible ? `${i * 120}ms` : "0ms"
          }}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 py-10 md:py-16 px-2 md:px-6">
                  {}
                  <div className="md:col-span-1">
                    <span className="font-mono font-medium text-secondary/40 group-hover:text-accent transition-colors duration-500 block leading-none" style={{
                  fontSize: "clamp(2rem, 4vw, 3rem)"
                }}>
                      {offering.number}
                    </span>
                  </div>

                  {}
                  <div className="md:col-span-5">
                    <h3 className="font-default font-bold text-foreground leading-tight mb-3" style={{
                  fontSize: "clamp(1.5rem, 3vw, 2.25rem)"
                }}>
                      <Link to={offering.linkTo} className="hover:text-accent transition-colors">
                        {offering.title}
                      </Link>
                    </h3>
                    <p className="font-mono text-xs md:text-sm uppercase tracking-wider text-secondary">
                      {offering.subtitle}
                    </p>
                  </div>

                  {}
                  <div className="md:col-span-5">
                    <p className="font-default text-base text-foreground/80 mb-6" style={{
                  lineHeight: 1.6
                }}>
                      {offering.description}
                    </p>
                    <ul className="space-y-2">
                      {offering.inclusions.map((inc, idx) => <li key={idx} data-index={idx} className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-secondary flex items-start gap-3">
                          <span className="text-accent mt-1.5 inline-block w-4 h-[2px] bg-accent shrink-0" />
                          <span>{inc}</span>
                        </li>)}
                    </ul>
                  </div>

                  {}
                  <div className="md:col-span-1 flex md:justify-end items-start mt-4 md:mt-2">
                    <Link to="/GetQuotePage" className="font-mono text-xs font-bold uppercase tracking-widest text-foreground hover:text-accent inline-flex items-center gap-2 transition-colors duration-300">
                      <span>Get a Quote</span>
                      <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover:translate-x-2" />
                    </Link>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}